import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { ZONES } from "@/lib/constants";
import { SERVICE_TYPES, URGENCY_OPTIONS } from "@/lib/serviceConstants";
import { HeartPulse, Send, ShieldCheck, Stethoscope } from "lucide-react";
import { toast } from "sonner";

export default function ServiceRequestPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    service_type: "",
    urgency: "normal",
    zone: "",
    address: "",
    preferred_date: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.service_type) return toast.error("Selecciona el tipo de servicio");
    if (!form.zone) return toast.error("Selecciona tu zona");
    setSubmitting(true);
    try {
      await api.post("/service-requests", form);
      toast.success("¡Solicitud enviada! Te contactaremos muy pronto.");
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo enviar la solicitud");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-[80vh]">
      <section className="bg-gradient-to-b from-blue-50 to-slate-50 border-b border-blue-100">
        <div className="max-w-4xl mx-auto px-4 py-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-blue-100 text-blue-800 text-xs font-medium">
            <Stethoscope className="w-3.5 h-3.5" /> Servicios de enfermería profesional
          </div>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-blue-950 tracking-tight">
            Cuidado profesional, en tu casa.
          </h1>
          <p className="mt-3 text-slate-700 text-base sm:text-lg max-w-2xl">
            Solicita un servicio de enfermería de Ciudad Enfermería. Te contactamos
            por WhatsApp para confirmar disponibilidad, costo y horario.
          </p>

          <div className="mt-6 grid sm:grid-cols-3 gap-3 max-w-2xl text-sm text-slate-700">
            <div className="flex items-start gap-2"><ShieldCheck className="w-5 h-5 text-blue-600 mt-0.5"/><span><strong>Personal certificado</strong><br/><span className="text-slate-500">Enfermeras tituladas</span></span></div>
            <div className="flex items-start gap-2"><HeartPulse className="w-5 h-5 text-blue-600 mt-0.5"/><span><strong>Trato humano</strong><br/><span className="text-slate-500">Cuidamos a tu familia</span></span></div>
            <div className="flex items-start gap-2"><Send className="w-5 h-5 text-blue-600 mt-0.5"/><span><strong>Respuesta rápida</strong><br/><span className="text-slate-500">Te llamamos al recibir</span></span></div>
          </div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={onSubmit} className="space-y-5">
          <Card className="p-5 sm:p-6 rounded-2xl border border-slate-200 space-y-4">
            <h2 className="font-display text-lg font-semibold text-slate-900">Tus datos</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-800">Nombre completo</Label>
                <Input required value={form.full_name} onChange={(e)=>setField("full_name", e.target.value)} className="h-12 mt-1.5 text-base bg-slate-50 focus:bg-white" placeholder="Ej. María Hernández" data-testid="service-name-input" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-800">WhatsApp / Teléfono</Label>
                <Input required value={form.phone} onChange={(e)=>setField("phone", e.target.value.replace(/[^0-9+ ]/g,""))} className="h-12 mt-1.5 text-base bg-slate-50 focus:bg-white" placeholder="+52 55 1234 5678" data-testid="service-phone-input" />
              </div>
            </div>
          </Card>

          <Card className="p-5 sm:p-6 rounded-2xl border border-slate-200 space-y-4">
            <h2 className="font-display text-lg font-semibold text-slate-900">Servicio que necesitas</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-800">Tipo de servicio</Label>
                <Select value={form.service_type} onValueChange={(v)=>setField("service_type", v)}>
                  <SelectTrigger className="h-12 mt-1.5 text-base bg-slate-50" data-testid="service-type-select">
                    <SelectValue placeholder="Selecciona…" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-800">Urgencia</Label>
                <Select value={form.urgency} onValueChange={(v)=>setField("urgency", v)}>
                  <SelectTrigger className="h-12 mt-1.5 text-base bg-slate-50" data-testid="service-urgency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {URGENCY_OPTIONS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-800">Zona</Label>
                <Select value={form.zone} onValueChange={(v)=>setField("zone", v)}>
                  <SelectTrigger className="h-12 mt-1.5 text-base bg-slate-50" data-testid="service-zone-select">
                    <SelectValue placeholder="Selecciona…" />
                  </SelectTrigger>
                  <SelectContent>
                    {ZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-800">Fecha preferida (opcional)</Label>
                <Input type="date" value={form.preferred_date} onChange={(e)=>setField("preferred_date", e.target.value)} className="h-12 mt-1.5 text-base bg-slate-50" data-testid="service-date-input" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-800">Domicilio (opcional)</Label>
              <Input value={form.address} onChange={(e)=>setField("address", e.target.value)} className="h-12 mt-1.5 text-base bg-slate-50" placeholder="Calle y colonia (lo confirmamos por WhatsApp)" data-testid="service-address-input" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-800">Detalles del paciente o servicio</Label>
              <Textarea rows={4} value={form.notes} onChange={(e)=>setField("notes", e.target.value)} className="mt-1.5 text-base bg-slate-50" placeholder="Ej. Mi mamá tiene 78 años, necesita curación diaria de herida postoperatoria…" data-testid="service-notes-textarea" />
            </div>
          </Card>

          <Button type="submit" disabled={submitting} className="w-full h-14 text-base bg-blue-600 hover:bg-blue-700 rounded-xl btn-press shadow-md" data-testid="service-submit-btn">
            <Send className="w-5 h-5 mr-2" />
            {submitting ? "Enviando…" : "Enviar solicitud"}
          </Button>
          <p className="text-center text-xs text-slate-500">Al enviar, aceptas que Ciudad Enfermería te contacte por los datos proporcionados.</p>
        </form>
      </div>
    </div>
  );
}
